import { Button, Flex, Skeleton, Typography } from "@smartfaster-ui/components";
import { type PropsWithChildren, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { refetchVocab, useVocab } from "../hooks/useVocab";
import { PanelFooterContext } from "./PanelFooterContext";

type PanelProps = PropsWithChildren<{
  ctaLabel?: string;
  ctaHintText?: string;
  ctaEnabled?: boolean;
  onCtaClick?: () => void;
  /**
   * When true, gates children behind vocab loading/error states.
   * Panels that don't need vocab data should set this to false.
   */
  requiresVocab?: boolean;
}>;

export function Panel({
  ctaLabel,
  ctaHintText,
  ctaEnabled,
  onCtaClick,
  children,
  requiresVocab = true,
}: PanelProps) {
  const { t } = useTranslation();
  const { isLoading, error } = useVocab();
  const setFooter = useContext(PanelFooterContext);

  const vocabBlocking = requiresVocab && (isLoading || !!error);
  const showContent = !vocabBlocking;

  // Keep the latest click handler in a ref so the footer effect doesn't depend
  // on its (per-render) identity, which would otherwise loop.
  const onCtaClickRef = useRef(onCtaClick);
  onCtaClickRef.current = onCtaClick;

  // Publish the CTA into the Drawer's fixed `trailing` footer so it stays
  // pinned at the bottom while the panel content scrolls.
  useEffect(() => {
    if (!ctaLabel || !showContent) {
      setFooter(null);
      return () => setFooter(null);
    }

    setFooter(
      <Flex direction="column" align="end" gap="xs" width="fill">
        {!ctaEnabled && ctaHintText && (
          <Typography variant="bodySm" color="foregroundSecondary">
            {ctaHintText}
          </Typography>
        )}
        <Button
          variant="filled"
          color="primary"
          disabled={!ctaEnabled}
          onClick={() => onCtaClickRef.current?.()}
        >
          {ctaLabel}
        </Button>
      </Flex>,
    );

    return () => setFooter(null);
  }, [setFooter, ctaLabel, ctaHintText, ctaEnabled, showContent]);

  return (
    <Flex direction="column" grow gap="md">
      {requiresVocab && isLoading && (
        <Flex direction="column" gap="sm" padding="sm">
          <Skeleton style={{ height: 120 }} />
          <Skeleton style={{ height: 120 }} />
          <Skeleton style={{ height: 60 }} />
        </Flex>
      )}

      {requiresVocab && error && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="sm"
          padding="lg"
        >
          <Typography
            variant="bodySm"
            color="foregroundSecondary"
            align="center"
          >
            {t("interiorDesigner.panel.error")}
          </Typography>
          <Button variant="outlined" size="sm" onClick={refetchVocab}>
            {t("interiorDesigner.panel.retry")}
          </Button>
        </Flex>
      )}

      {showContent && children}
    </Flex>
  );
}
